import React from 'react'
import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import RedeemIcon from '@mui/icons-material/Redeem'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import VerifiedIcon from '@mui/icons-material/Verified'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import DescriptionIcon from '@mui/icons-material/Description'
import { formatNumber } from 'src/common/helpers'
import { ClaimStatus, EarningStatement, MintStatus } from 'src/common/api'
import { formatRate, TOOLTIPS } from '../../utils'
import TxHashLink from '../TxHashLink/TxHashLink'

interface StatementsTableProps {
  statements: EarningStatement[]
  onClaim: (statement: EarningStatement) => void
  onDownloadPdf: (statementId: string) => void
  downloadingId: string | null
}

type Align = 'left' | 'right' | 'center'

const HeaderCell: React.FC<{ label: string; tip?: string; align?: Align }> = ({ label, tip, align = 'left' }) => (
  <TableCell align={align} sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' }}>
    <Box
      component="span"
      display="inline-flex"
      alignItems="center"
      gap={0.25}
      justifyContent={align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start'}
    >
      {label}
      {tip && (
        <Tooltip title={tip} arrow>
          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'default' }} />
        </Tooltip>
      )}
    </Box>
  </TableCell>
)

const MintStatusLine: React.FC<{ statement: EarningStatement }> = ({ statement }) => {
  if (statement.mintStatus === MintStatus.INITIATED) {
    return (
      <Chip
        size="small"
        label="Mint Initiated"
        color="warning"
        variant="outlined"
        sx={{ height: 22, fontSize: 11, width: 'fit-content', alignSelf: 'center' }}
      />
    )
  }
  if (statement.mintStatus === MintStatus.QUEUED) {
    return (
      <Chip
        size="small"
        label="Mint Queued"
        color="info"
        variant="outlined"
        sx={{ height: 22, fontSize: 11, width: 'fit-content', alignSelf: 'center' }}
      />
    )
  }
  if (
    statement.mintStatus === MintStatus.CANCELLED ||
    statement.mintStatus === MintStatus.FAILED ||
    statement.mintStatus === MintStatus.DECLINED ||
    statement.mintStatus === MintStatus.EXPIRED
  ) {
    return (
      <Chip
        size="small"
        label={'Mint ' + statement.mintStatus.charAt(0).toUpperCase() + statement.mintStatus.slice(1)}
        color="error"
        variant="outlined"
        sx={{ height: 22, fontSize: 11, width: 'fit-content', alignSelf: 'center' }}
      />
    )
  }
  if (statement.mintStatus === MintStatus.PROCESSED) {
    return (
      <Box display="flex" flexDirection="column" gap={0.5} alignSelf="center">
        <Box display="flex" alignItems="center" gap={0.75}>
          <VerifiedIcon sx={{ fontSize: 16, color: 'success.dark' }} />
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'success.dark' }}>Mint Processed</Typography>
          {statement.mintTxDate && (
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{statement.mintTxDate}</Typography>
          )}
        </Box>
        {statement.mintTxHash && (
          <Box pl={2.5}>
            <TxHashLink hash={statement.mintTxHash} chain={statement.mintTxChain} />
          </Box>
        )}
      </Box>
    )
  }
  return null
}

const StatementsTable: React.FC<StatementsTableProps> = ({ statements, onClaim, onDownloadPdf, downloadingId }) => {
  const renderClaimMint = (statement: EarningStatement) => {
    if (statement.claimStatus === ClaimStatus.UNCLAIMED) {
      return (
        <Button variant="contained" size="small" startIcon={<RedeemIcon />} onClick={() => onClaim(statement)}>
          Mint Earnings
        </Button>
      )
    }
    return (
      <Box display="flex" flexDirection="column" gap={1}>
        <Box display="flex" alignItems="center" alignSelf="center" gap={0.75}>
          <CheckCircleIcon sx={{ fontSize: 16, color: 'primary.main' }} />
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'primary.main' }}>
            {statement.claimedBy ? `Claimed by ${statement.claimedBy}` : 'Claimed'}
          </Typography>
        </Box>
        {statement.updatedAt && (
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
            Updated at:{' '}
            {new Date(statement.updatedAt).toLocaleString('en-US', {
              timeZone: 'America/New_York',
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              timeZoneName: 'short'
            })}
          </Typography>
        )}
        <MintStatusLine statement={statement} />
      </Box>
    )
  }

  return (
    <Paper variant="card" sx={{ borderRadius: 1, p: 3, mb: 3 }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <DescriptionIcon color="primary" />
        <Typography variant="h6" fontWeight={600} fontSize={16}>
          Monthly Statements
        </Typography>
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <HeaderCell label="Period" />
              <HeaderCell label="Statement Date" />
              <HeaderCell label="Avg. Daily Balance (QCAD) / MTD" tip={TOOLTIPS.avgDailyBalance} align="right" />
              <HeaderCell label="Net Rate" tip={TOOLTIPS.effectiveRate} align="right" />
              <HeaderCell label="Gross QCAD Earned" tip={TOOLTIPS.grossAccrual} align="right" />
              <HeaderCell label="Allowable Expenses (QCAD)" tip={TOOLTIPS.allowableExpenses} align="right" />
              <HeaderCell label="Net QCAD Earned" tip={TOOLTIPS.netRevenuePayout} align="right" />
              <HeaderCell label="QCAD Earned - Claim & Mint" align="center" />
              <HeaderCell label="PDF" align="center" />
            </TableRow>
          </TableHead>
          <TableBody>
            {statements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No statements available yet.
                </TableCell>
              </TableRow>
            ) : (
              statements.map((statement) => (
                <TableRow key={statement.id} hover>
                  <TableCell sx={{ fontWeight: 700 }}>{statement.period}</TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{statement.statementDate}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {formatNumber(statement.avgDailyBalanceQcad)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {formatRate(statement.effectiveRate)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {formatNumber(statement.grossAccrualQcad)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                    {formatNumber(statement.allowableExpensesQcad)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 800, color: 'primary.main' }}>
                    {formatNumber(statement.netRevenuePayoutQcad)}
                  </TableCell>
                  <TableCell align="center">{renderClaimMint(statement)}</TableCell>
                  <TableCell align="center">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PictureAsPdfIcon />}
                      loading={downloadingId === statement.id}
                      disabled={!statement.pdfAvailable}
                      onClick={() => onDownloadPdf(statement.id)}
                    >
                      Download
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}

export default StatementsTable
